import React, { useCallback, useMemo, useState } from "react";
import {
    Calendar,
    Culture,
    DateLocalizer,
    EventPropGetter,
    SlotInfo,
    View,
    Views,
    momentLocalizer,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useLoaderData } from "react-router";

import moment from "moment";
import "moment-timezone";

import AddGameModal from "./components/Calendar/AddGameModal";
import DeleteGameModal from "./components/Calendar/DeleteGameModal";
import EditGameModal from "./components/Calendar/EditGameModal";
import Toolbar from "./components/Calendar/Toolbar";
import TournamentView from "./components/Calendar/TournamentView";

import { store } from "../..";
import { alertActions } from "../../features/alert/alertSlice";
import { globalActions } from "../../features/global/globalSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    getTemplate,
    getTournamentSchedule,
    rescheduleGame,
} from "../../lib/api";
import { AUTH_LEVELS } from "../../lib/globals";
import { isChildTournament } from "../../lib/utils";
import {
    IGame,
    ILeagueStanding,
    IMasterTournament,
    IMasterTournamentSchedule,
    ITournament,
    ITournamentSchedule,
    ITournamentTemplate,
} from "../../types/api";
import { ICalendarEvent, ICalendarResource } from "../../types/shared";

const isGame = (obj: any): obj is IGame => {
    return (
        obj &&
        obj.hasOwnProperty("tournament") &&
        obj.tournament !== undefined &&
        obj.tournament !== null &&
        typeof obj.tournament === "number"
    );
};

const addEvent = (g: IGame, tournament: ITournament): ICalendarEvent => {
    let endDate: Date;
    let title: Array<string | React.ReactNode> = [];

    const startDate = new Date(g.date);
    if (g.endDate) {
        endDate = new Date(g.endDate);
    } else {
        endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 2);
        endDate.setMinutes(endDate.getMinutes() + 15);
    }

    if (g.consGameInfo) {
        title.push(
            <>
                <b>Consolation #{g.consGameInfo.playoffGameId}</b>
                <br />
            </>,
        );
    } else if (g.champGameInfo) {
        title.push(
            <>
                <b>Championship #{g.champGameInfo.playoffGameId}</b>
                <br />
            </>,
        );
    }

    const playoffGameInfo = g.consGameInfo || g.champGameInfo || null;

    if (g.visitor.name && g.home.name) {
        if (playoffGameInfo) {
            let standing: string = "";
            if ((tournament as ITournament).standings !== null) {
                const visitorStanding = (
                    (tournament as ITournament).standings as Record<
                        number,
                        ILeagueStanding
                    >
                )[g.visitor.id].standing;
                const homeStanding = (
                    (tournament as ITournament).standings as Record<
                        number,
                        ILeagueStanding
                    >
                )[g.home.id].standing;
                standing = `#${visitorStanding} ${g.visitor.name} vs. #${homeStanding} ${g.home.name}`;
            }
            title.push(`${standing}`);
        } else {
            title.push(g.visitor.name);
            title.push(" vs. ");
            title.push(g.home.name);
        }
    } else {
        if (playoffGameInfo) {
            if (
                playoffGameInfo.matchupTitle &&
                playoffGameInfo.matchupTitle !== "null"
            ) {
                title.push(playoffGameInfo.matchupTitle);
            } else {
                if (playoffGameInfo.visitor.seed) {
                    title.push(`#${playoffGameInfo.visitor.seed}`);
                } else if (playoffGameInfo.visitor.winnerOf) {
                    const bracket =
                        playoffGameInfo.bracket.type === "Championship"
                            ? "Champ."
                            : "Cons.";
                    title.push(
                        `Winner of ${bracket} ${playoffGameInfo.visitor.winnerOf}`,
                    );
                }

                if (playoffGameInfo.home.seed) {
                    title.push(`#${playoffGameInfo.home.seed}`);
                } else if (playoffGameInfo.home.winnerOf) {
                    const bracket =
                        playoffGameInfo.bracket.type === "Championship"
                            ? "Champ."
                            : "Cons.";
                    title.push(
                        `Winner of ${bracket} ${playoffGameInfo.home.winnerOf}`,
                    );
                }
            }
        } else {
            title.push("TBD vs. TBD");
        }
    }

    return {
        id: g.id,
        start: startDate,
        end: endDate,
        title,
        resourceId: g.diamond || 0,
        ageLevel: g.ageLevel.ageLevel,
        umpires: g.umpires,
        home: g.home,
        visitor: g.visitor,
    };
};

const Cal = withDragAndDrop(Calendar<ICalendarEvent, ICalendarResource>);

export const loader = async ({ params }: any) => {
    const tournamentId = parseInt(params.tournamentId);

    store.dispatch(
        tournamentManagementActions.setselectedTournamentId(
            parseInt(params.tournamentId),
        ),
    );

    const fields = store.getState().global.fields;
    const umpires = store.getState().global.umpires;
    const settings = store.getState().global.settings;

    let tournaments = store.getState().tournamentmanagement.tournaments;
    let masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getTournaments());

    const masterTournamentsPromise = masterTournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    const fieldsPromise = fields.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getFields());

    const umpiresPromise = umpires.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getUmpires());

    const settingsPromise = settings.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getSettings());

    const [{ schedule }, { template }] = await Promise.all([
        getTournamentSchedule(tournamentId),
        getTemplate("tournament", tournamentId),
        fieldsPromise,
        tournamentsPromise,
        masterTournamentsPromise,
        umpiresPromise,
        settingsPromise,
    ]);

    const tournament = selectSelectedTournament(store.getState());
    tournaments = store.getState().tournamentmanagement.tournaments;
    masterTournaments = store.getState().tournamentmanagement.masterTournaments;

    const masterTournament = isChildTournament(tournament)
        ? masterTournaments.find(
              mt =>
                  mt.id ===
                  tournaments.find(t => t.id === tournamentId)?.parent,
          )
        : tournament;

    const header = isChildTournament(tournament)
        ? `${masterTournament?.name} - ${tournament.ageLevel} Tournament Calendar`
        : `${tournament?.name} - Full Tournament Calendar`;

    return {
        schedule,
        header,
        template,
    };
};

const localizer = momentLocalizer(moment);

const TournamentCalendar = (): React.JSX.Element => {
    const { schedule, template } = useLoaderData() as {
        schedule: ITournamentSchedule | IMasterTournamentSchedule;
        template: ITournamentTemplate;
    };

    const [showGameModal, setShowGameModal] = useState(false);
    const [modalGame, setModalGame] = useState<ICalendarEvent>();
    const user = useAppSelector(state => state.auth.user);
    const tournament = useAppSelector(selectSelectedTournament);
    const { tournaments } = useAppSelector(state => state.tournamentmanagement);
    const { fields } = useAppSelector(state => state.global);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<ICalendarEvent>();
    const [eventToAdd, setEventToAdd] = useState<SlotInfo>();
    const dispatch = useAppDispatch();

    const resources = useMemo(() => {
        const result: {
            resourceTitle: string;
            resourceId: number;
        }[] = [];

        for (const key in template.template.diamondRestrictions) {
            result.push({
                resourceTitle:
                    fields
                        .find(f => f.id === template.field)
                        ?.diamonds.find(d => d.id === parseInt(key))?.name ||
                    `Field ${key}`,
                resourceId: parseInt(key) || 0,
            });
        }
        return result;
    }, [fields, template]);

    const [events, setEvents] = useState<Array<ICalendarEvent>>(() => {
        const events: Array<ICalendarEvent> = [];

        schedule.games?.map(g => {
            if (isGame(g)) {
                // For child tournamnet calendars
                events.push(addEvent(g, tournament as ITournament));
            } else {
                // For master tournamnet calendars
                g.games?.map(cg => {
                    const childTournament = tournaments.find(
                        t =>
                            t.parent === (tournament as IMasterTournament).id &&
                            t.ageLevel === g.ageLevel,
                    );
                    events.push(addEvent(cg, childTournament as ITournament));
                });
            }
        });
        return events;
    });

    const convertDate = useCallback((dateString: string, hours: number) => {
        const date = new Date(dateString);
        date.setHours(hours, 0, 0, 0);
        return date;
    }, []);

    const handleMove = useCallback(
        async ({
            event,
            start,
            end,
            resourceId,
        }: {
            event: ICalendarEvent;
            start: Date | string;
            end: Date | string;
            resourceId?: string | number;
        }) => {
            setEvents(prev => {
                const existing =
                    prev.find(ev => ev.id === event.id) ??
                    ({} as ICalendarEvent);
                const filtered = prev.filter(ev => ev.id !== event.id);
                return [...filtered, { ...existing, start, end, resourceId }];
            });

            rescheduleGame(
                {
                    gameId: event.id,
                    start: start.toLocaleString("en-US"),
                    end: end.toLocaleString("en-US"),
                    field: tournament?.field?.id as number,
                    diamond: resourceId as number,
                },
                user!.id,
            )
                .then(response => {
                    dispatch(
                        alertActions.success({
                            title: `Game rescheduled successfully`,
                            dismissible: true,
                            scroll: false,
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem rescheduling the game.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        },
        [setEvents],
    );

    const handleResize = useCallback(
        ({
            event,
            start,
            end,
        }: {
            event: ICalendarEvent;
            start: Date | string;
            end: Date | string;
        }) => {
            setEvents(prev => {
                const existing =
                    prev.find(ev => ev.id === event.id) ||
                    ({} as ICalendarEvent);
                const filtered = prev.filter(ev => ev.id !== event.id);
                return [...filtered, { ...existing, start, end }];
            });
            rescheduleGame(
                {
                    gameId: event.id,
                    start: start.toLocaleString("en-US"),
                    end: end.toLocaleString("en-US"),
                    field: tournament?.field?.id as number,
                    diamond: event.resourceId as number,
                },
                user!.id,
            )
                .then(response => {
                    dispatch(
                        alertActions.success({
                            title: `Game rescheduled successfully`,
                            dismissible: true,
                            scroll: false,
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem rescheduling the game.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        },
        [setEvents],
    );

    const { defaultDate, defaultView, views, formats, minDate, maxDate } =
        useMemo(
            () => ({
                defaultDate: tournament?.startDate
                    ? new Date(tournament.startDate)
                    : new Date(),
                defaultView: Views.DAY,
                views: { day: true, week: TournamentView },
                formats: {
                    dayFormat: (
                        date: Date,
                        culture?: Culture,
                        localizer?: DateLocalizer,
                    ) => localizer?.format(date, "ddd M/DD", culture) || "",
                },
                minDate: tournament?.startDate
                    ? convertDate(tournament.startDate, 7)
                    : new Date(),
                maxDate: tournament?.endDate
                    ? convertDate(tournament.startDate, 23)
                    : new Date(),
            }),
            [],
        );

    const [view, setView] = useState<View>(defaultView);
    const onView = useCallback((newView: View) => setView(newView), [setView]);

    const eventPropGetter: EventPropGetter<ICalendarEvent> = useCallback(
        event => ({ className: `tournamentAge${event.ageLevel}` }),
        [],
    );

    return (
        <React.Fragment>
            <Cal
                components={{
                    toolbar: props => (
                        <Toolbar
                            isUmpireSchedule={false}
                            events={events}
                            {...props}
                        />
                    ),
                }}
                defaultDate={defaultDate}
                defaultView={defaultView}
                draggableAccessor={() =>
                    user
                        ? AUTH_LEVELS.sitemanagement.includes(user?.userLevel)
                        : false
                }
                events={events}
                eventPropGetter={eventPropGetter}
                formats={formats}
                localizer={localizer}
                max={maxDate}
                messages={{
                    week: "Tournament Calendar",
                    agenda: "Tournament Schedule",
                }}
                min={minDate}
                onEventDrop={handleMove}
                onEventResize={handleResize}
                onSelectEvent={props => {
                    setModalGame(props);
                    setShowGameModal(true);
                }}
                onView={onView}
                onSelectSlot={props => {
                    if (
                        !user ||
                        !AUTH_LEVELS.sitemanagement.includes(user?.userLevel)
                    ) {
                        return;
                    }

                    const diff = Math.abs(
                        props.start.getTime() - props.end.getTime(),
                    );
                    if (diff >= 60 * 60 * 1000) {
                        setEventToAdd(props);
                        setShowAddModal(true);
                    }
                }}
                resizable={
                    user
                        ? AUTH_LEVELS.sitemanagement.includes(user?.userLevel)
                        : false
                }
                resources={resources}
                resourceIdAccessor="resourceId"
                resourceTitleAccessor="resourceTitle"
                selectable={
                    user
                        ? AUTH_LEVELS.sitemanagement.includes(user?.userLevel)
                        : false
                }
                step={15}
                timeslots={4}
                view={view}
                views={views}
            />
            {showDeleteModal && eventToDelete && (
                <DeleteGameModal
                    {...eventToDelete}
                    resources={resources}
                    show={showDeleteModal}
                    setShow={setShowDeleteModal}
                    setEvents={setEvents}
                    gameType="game"
                />
            )}
            {showAddModal && eventToAdd && (
                <AddGameModal
                    {...eventToAdd}
                    resources={resources}
                    show={showAddModal}
                    setShow={setShowAddModal}
                    setEvents={setEvents}
                    gameType="game"
                />
            )}
            {showGameModal && modalGame && (
                <EditGameModal
                    {...modalGame}
                    resources={resources}
                    show={showGameModal}
                    setShow={setShowGameModal}
                    events={events}
                    setEvents={setEvents}
                    setShowDelete={setShowDeleteModal}
                    setEventToDelete={setEventToDelete}
                />
            )}
        </React.Fragment>
    );
};

export default TournamentCalendar;
